/**
 * Nén ảnh phía client trước khi tải lên — ảnh điện thoại thường 4000px cạnh dài, tải nguyên bản
 * vừa chậm vừa tốn băng thông 3G của người dùng. Resize bằng canvas nếu cạnh dài > MAX_EDGE.
 */

export const MAX_EDGE_PX = 2000;
export const MAX_INPUT_BYTES = 30 * 1024 * 1024; // 30MB — chặn sớm trước khi cố load/nén

export class PhotoInputError extends Error {
  constructor(public readonly reason: "too_large" | "load_failed" | "encode_failed") {
    super(reason);
    this.name = "PhotoInputError";
  }
}

function loadImageElement(file: File | Blob): Promise<{ img: HTMLImageElement; url: string }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new PhotoInputError("load_failed"));
    };
    img.src = url;
  });
}

export type CompressedImage = {
  blob: Blob;
  width: number;
  height: number;
  mimeType: string;
  previewUrl: string;
};

/**
 * Đọc + resize ảnh nếu cần, trả về blob đã nén (JPEG) kèm URL xem trước. Cùng một blob này được
 * dùng xuyên suốt luồng (xem trước, tải lên, chồng QR) để toạ độ luôn khớp với ảnh thực tế đã
 * gửi lên server.
 */
export async function compressImageFile(file: File, maxEdge = MAX_EDGE_PX, quality = 0.85): Promise<CompressedImage> {
  if (file.size > MAX_INPUT_BYTES) {
    throw new PhotoInputError("too_large");
  }

  const { img, url } = await loadImageElement(file);
  try {
    const naturalWidth = img.naturalWidth || img.width;
    const naturalHeight = img.naturalHeight || img.height;
    if (!naturalWidth || !naturalHeight) {
      throw new PhotoInputError("load_failed");
    }

    const longEdge = Math.max(naturalWidth, naturalHeight);
    const scale = longEdge > maxEdge ? maxEdge / longEdge : 1;
    const width = Math.max(1, Math.round(naturalWidth * scale));
    const height = Math.max(1, Math.round(naturalHeight * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new PhotoInputError("encode_failed");
    ctx.drawImage(img, 0, 0, width, height);

    const mimeType = "image/jpeg";
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new PhotoInputError("encode_failed"))),
        mimeType,
        quality,
      );
    });

    const previewUrl = URL.createObjectURL(blob);
    return { blob, width, height, mimeType, previewUrl };
  } finally {
    URL.revokeObjectURL(url);
  }
}
